// reads and resolves the automated rule-guardrail reports shown at
// /admin/moderation.
//
// promptModerationFunctions.js writes a moderationReports/{promptID} doc every
// time a published prompt trips a rule from eropenpal.com/rules. A `block`
// verdict has already hidden the prompt; a `review` verdict leaves it live and
// this queue is the ONLY place it surfaces, which is the whole reason the page
// exists.
//
// Only admins can read this collection - the reports name which pattern fired,
// which is a map of how to evade the engine.
import {
  doc,
  collection,
  collectionGroup,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  updateDoc,
  deleteField,
  query,
  orderBy,
  limit,
  serverTimestamp
} from 'firebase/firestore';
import {
  GET_MODERATION_REPORTS,
  REMOVE_MODERATION_REPORT,
  UPDATE_MODERATION_REPORT
} from './types';
import { firestore } from '../firebase';
import {
  asyncActionStart,
  asyncActionFinish,
  asyncActionError
} from './asyncActions';
import { openSnackbar } from './snackbarActions';

// the queue is meant to be worked through and emptied; if it is ever longer
// than this the answer is a pattern fix, not a bigger page
export const REPORTS_LIMIT = 200;

// Pull the per-prompt user-flag tallies. The counter lives at
// prompts/{id}/flags/reasonCounter, maintained by handleFlagPromptCounter, and
// there is no way to query "only the counters" across a collection group, so we
// read the flags group and keep the counter docs. Small today (82 docs on prod);
// if that ever stops being true the fix is a top-level mirror, not a bigger read.
const fetchUserFlags = async () => {
  const snapshots = await getDocs(collectionGroup(firestore, 'flags'));
  const byPrompt = {};
  // The individual flags, alongside the tallies. This read already pulled them
  // down - the old version filtered them out and threw them away - and they
  // carry the one thing a counter cannot: what the reporter actually WROTE.
  // "User posts link to his personal address for underage play" decides a case;
  // "underage: 1" only says somebody clicked something.
  const detailByPrompt = {};
  snapshots.docs.forEach(docSnap => {
    const promptID = docSnap.ref.parent.parent && docSnap.ref.parent.parent.id;
    if (!promptID) return;

    if (docSnap.id !== 'reasonCounter') {
      // handleFlagPrompt writes one doc per reporter, keyed BY the reporter's
      // uid - so the doc id is who filed it, and there can only be one per
      // person per prompt
      const flag = docSnap.data() || {};
      if (!detailByPrompt[promptID]) detailByPrompt[promptID] = [];
      detailByPrompt[promptID].push({
        reporterID: docSnap.id,
        reason: flag.reason || null,
        comment: typeof flag.comment === 'string' ? flag.comment.trim() : '',
        createdAt: flag.createdAt || null
      });
      return;
    }

    const data = docSnap.data() || {};
    const reasons = {};
    let total = 0;
    Object.keys(data).forEach(key => {
      // `reporters` is the idempotency map, not a tally
      if (key === 'reporters') return;
      if (typeof data[key] !== 'number' || data[key] <= 0) return;
      reasons[key] = data[key];
      total += data[key];
    });
    if (total > 0) byPrompt[promptID] = { reasons, total };
  });

  // second pass: the detail docs and the counter arrive in one unordered
  // snapshot, so the counters have to exist before anything can be attached
  Object.keys(byPrompt).forEach(promptID => {
    byPrompt[promptID].flags = detailByPrompt[promptID] || [];
  });
  return byPrompt;
};

export const getModerationReports = () => async dispatch => {
  dispatch(asyncActionStart());
  try {
    const reportsRef = collection(firestore, 'moderationReports');
    const queryRef = query(
      reportsRef,
      orderBy('createdAt', 'desc'),
      limit(REPORTS_LIMIT)
    );
    const querySnapshots = await getDocs(queryRef);

    const reports = querySnapshots.docs.map(docSnap => {
      const { id } = docSnap;
      return { ...docSnap.data(), id };
    });

    // Two independent signals answer the same question - "which prompts need a
    // human?" - so they share one queue. Keeping them on separate pages
    // guarantees one of them gets ignored, which is exactly what happened to the
    // user flags: 30 flagged prompts had never been visible anywhere.
    const [userFlags, dismissalSnapshots] = await Promise.all([
      fetchUserFlags(),
      getDocs(collection(firestore, 'flagDismissals'))
    ]);
    const dismissals = {};
    dismissalSnapshots.docs.forEach(docSnap => {
      dismissals[docSnap.id] = docSnap.data() || {};
    });

    const byId = {};
    reports.forEach(report => {
      byId[report.id] = report;
    });
    Object.keys(userFlags).forEach(promptID => {
      const dismissed = dismissals[promptID];
      // a dismissal only holds while the flag count has not grown, so new flags
      // bring the row back instead of it being silently buried forever
      if (dismissed && userFlags[promptID].total <= (dismissed.flagTotalAtDismissal || 0)) {
        return;
      }
      if (!byId[promptID]) byId[promptID] = { id: promptID, verdict: null, violations: [] };
      byId[promptID].userFlags = userFlags[promptID];
    });
    const merged = Object.keys(byId).map(id => byId[id]);

    // Pull each prompt's body alongside its report so hovering a title shows
    // the text, the same triage affordance /admin/prompts has. The report
    // deliberately does not store a copy of the text - it would go stale the
    // moment the author edited the prompt, and this page is where someone
    // decides whether to delete that prompt, so it has to show what is
    // actually there now.
    // One read per row, bounded by REPORTS_LIMIT. A report can outlive its
    // prompt (deleted elsewhere), which is why promptMissing exists.
    const withText = await Promise.all(
      merged.map(async report => {
        try {
          const promptSnap = await getDoc(doc(firestore, 'prompts', report.id));
          if (!promptSnap.exists()) return { ...report, promptMissing: true };
          const prompt = promptSnap.data();
          // a flag-only row has no report document, so the prompt IS the source
          // of its title, author and current visibility
          return {
            ...report,
            promptText: prompt.text,
            title: report.title || prompt.title || null,
            creator: report.creator || prompt.creator || null,
            creatorID: report.creatorID || prompt.creatorID || null,
            published: report.published !== undefined ? report.published : prompt.published === true,
            hidden: prompt.hide === true,
            hiddenReason: prompt.hiddenReason || null,
            hiddenDetail: prompt.hiddenDetail || null
          };
        } catch (error) {
          // a failed preview must not cost us the row itself
          return { ...report, promptText: '' };
        }
      })
    );

    dispatch({
      type: GET_MODERATION_REPORTS,
      payload: { moderationReports: withText }
    });
    dispatch(asyncActionFinish());
  } catch (error) {
    console.error(error);
    dispatch(asyncActionError());
    dispatch(
      openSnackbar('ErrorSnackbar', {
        message: 'Could not load the moderation queue.'
      })
    );
  }
};

// "this one is fine" - drops the report and leaves the prompt exactly as it is.
// Deliberately does not un-hide anything: unhiding is a separate, explicit
// decision, because `hide` is shared with the user-flag system.
export const dismissModerationReport = promptID => async dispatch => {
  try {
    await deleteDoc(doc(firestore, 'moderationReports', promptID));
    dispatch({ type: REMOVE_MODERATION_REPORT, payload: { promptID } });
    dispatch(
      openSnackbar('SuccessSnackbar', { message: 'Dismissed the report.' })
    );
  } catch (error) {
    console.error(error);
    dispatch(
      openSnackbar('ErrorSnackbar', {
        message: 'Could not dismiss the report.'
      })
    );
  }
};

// Delete the prompt outright, and its report with it. This is a hard delete -
// the same one /admin/prompts performs - and there is no undo here, so the
// button that calls it asks twice.
//
// Deleting the report too because a report pointing at a prompt that no longer
// exists is a dead row in the queue: the page links to it and an admin cannot
// tell "already dealt with" from "still up".
export const deleteModeratedPrompt = (promptID, promptTitle) => async dispatch => {
  try {
    await deleteDoc(doc(firestore, 'prompts', promptID));
    // the report is best-effort: the prompt is what mattered, and a leftover
    // report is recoverable noise rather than a failure worth surfacing
    await deleteDoc(doc(firestore, 'moderationReports', promptID)).catch(() => {});
    dispatch({ type: REMOVE_MODERATION_REPORT, payload: { promptID } });
    dispatch(
      openSnackbar('SuccessSnackbar', {
        message: `Deleted "${promptTitle || promptID}".`
      })
    );
  } catch (error) {
    console.error(error);
    dispatch(
      openSnackbar('ErrorSnackbar', {
        message: 'Could not delete the prompt.'
      })
    );
  }
};

// "I looked at these user flags and the prompt is fine." Records the flag count
// at the time, so a later flag brings the row back rather than the prompt being
// dismissed permanently on the strength of one review.
export const dismissPromptFlags = (promptID, flagTotal) => async (dispatch, getState) => {
  try {
    const { auth } = getState();
    await setDoc(doc(firestore, 'flagDismissals', promptID), {
      promptID,
      flagTotalAtDismissal: flagTotal || 0,
      dismissedAt: serverTimestamp(),
      dismissedBy: (auth && auth.currentUser && auth.currentUser.uid) || null
    });
    dispatch({ type: REMOVE_MODERATION_REPORT, payload: { promptID } });
    dispatch(
      openSnackbar('SuccessSnackbar', {
        message: 'Cleared the user flags from the queue.'
      })
    );
  } catch (error) {
    console.error(error);
    dispatch(
      openSnackbar('ErrorSnackbar', { message: 'Could not clear the flags.' })
    );
  }
};

// Show or hide the prompt itself. This is the action that undoes a false
// positive: the engine hid something it should not have, and an admin puts it
// back. The report stays until it is dismissed, so the decision is auditable.
//
// Refuses to unhide a prompt a BAN took down. Doing so would put the prompt back
// on the site while its author still cannot sign in, and a later unban would
// find nothing to restore. Unbanning the author is the action that belongs here.
export const setPromptHidden = (promptID, hide, hiddenReason) => async (
  dispatch,
  getState
) => {
  if (!hide && hiddenReason === 'ban') {
    dispatch(
      openSnackbar('ErrorSnackbar', {
        message:
          'This prompt was hidden by a ban. Unban the author instead - that restores their prompts.',
        autoHideDuration: 9000
      })
    );
    return;
  }
  try {
    const { auth } = getState();
    // record WHY, in the same write that sets hide. Without this an admin hide
    // is indistinguishable afterwards from one the flag system or the rule
    // engine performed, and the cause has to be guessed from other signals.
    const changes = hide
      ? {
          hide: true,
          hiddenReason: 'admin',
          hiddenDetail: 'hidden by an admin from /admin/moderation',
          hiddenAt: serverTimestamp(),
          hiddenBy: (auth && auth.currentUser && auth.currentUser.uid) || null
        }
      : {
          hide: false,
          hiddenReason: deleteField(),
          hiddenDetail: deleteField(),
          hiddenAt: deleteField(),
          hiddenBy: deleteField()
        };
    await updateDoc(doc(firestore, 'prompts', promptID), changes);
    dispatch({
      type: UPDATE_MODERATION_REPORT,
      payload: {
        promptID,
        changes: { hidden: hide, hiddenReason: hide ? 'admin' : null }
      }
    });
    dispatch(
      openSnackbar('SuccessSnackbar', {
        message: hide ? 'Hid the prompt.' : 'Made the prompt visible again.'
      })
    );
  } catch (error) {
    console.error(error);
    dispatch(
      openSnackbar('ErrorSnackbar', {
        message: 'Could not change the prompt. Is it still there?'
      })
    );
  }
};
